import { useState, useEffect, useCallback } from 'react';
import AllergensAndSensitivitiesService from '@/services/allergensAndSensitivitiesService';
import { useGlobalStateContext } from '@/context/GlobalContext';
import type {
  AttributeLookupOnloadResponse,
  AllergenDetailResponse
} from '@/components/ActiveProjects/AllergensAndSensitivities/types';
import type { AllergenData } from '@/hooks/useAllergens';
import { handleApiError } from '@/hooks/useApiErrorHandler';

export interface DropdownOptions {
  riskStatementOptions: Array<{ label: string; value: string | number }>;
  labelDeclarationOptions: Array<{ label: string; value: string | number }>;
  specifyOptions: Array<{ label: string; value: string | number }>;
}

export interface UseAllergenEditDataProps {
  open: boolean;
  allergenData?: AllergenData;
  errorTexts?: {
    FAILED_TO_LOAD_DROPDOWN_OPTIONS?: string;
    FAILED_TO_LOAD_ALLERGEN_DETAILS?: string;
  };
}

export interface UseAllergenEditDataReturn {
  dropdownOptions: DropdownOptions;
  allergenDetail: AllergenDetailResponse | null;
  isLoadingOptions: boolean;
  isLoadingDetail: boolean;
  refetchDropdownOptions: () => Promise<void>;
  refetchAllergenDetail: () => Promise<void>;
}

/**
 * Custom hook to handle all data fetching for Allergen Edit Modal
 * Centralizes API calls and uses global loading context
 */
export const useAllergenEditData = ({
  open,
  allergenData,
  errorTexts
}: UseAllergenEditDataProps): UseAllergenEditDataReturn => {
  const { setIsLoading } = useGlobalStateContext();
  
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptions>({
    riskStatementOptions: [],
    labelDeclarationOptions: [],
    specifyOptions: []
  });
  
  const [allergenDetail, setAllergenDetail] = useState<AllergenDetailResponse | null>(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [hasLoadedDetail, setHasLoadedDetail] = useState(false);

  /**
   * Fetch dropdown options for the allergen edit form
   * API: GET /attributeLookup/getAttributeLookupListing/...
   */
  const fetchDropdownOptions = useCallback(async () => {
    if (!open) return;

    setIsLoadingOptions(true);
    setIsLoading(true); // Global loading indicator
    
    try {
      // Specify options: use `label` from glossary onload (allergyDietaryQuestions.label).
      // This replaces the old ID-based mapping file and lets backend drive the lookup key.
      const allergenId = allergenData?.id;
      const labelKey = String(allergenData?.label ?? '').trim();
      const additionalParams = labelKey ? [labelKey] : [];

    

      // Fallback: if labelKey isn't available, request the common category keys (legacy behavior).
      const finalParams =
        additionalParams.length > 0
          ? additionalParams
          : ['nuts', 'crustacean', 'fish', 'cereals', 'milk', 'molluscs', 'egg'];
      
      // Fetch data with dynamic parameters for specify options
      const data: AttributeLookupOnloadResponse = await AllergensAndSensitivitiesService.getAttributeOnloadData(finalParams);

      // Transform API data to dropdown options format
      const riskStatementOpts = data.statementToBestDescribe?.map((item) => ({
        label: item.name,
        value: item.id
      })) || [];

      const labelDeclarationOpts = data.labelDeclaration?.map((item) => ({
        label: item.name,
        value: item.id
      })) || [];

      // Combine specify options from available categories based on the resolved lookup keys
      const specifyOpts: Array<{ label: string; value: string | number }> = [];

      finalParams.forEach((key) => {
        const items = (data as unknown as Record<string, unknown>)[key];
        if (!Array.isArray(items)) return;
        specifyOpts.push(
          ...(items as Array<{ name: string; id: string | number }>).map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );
      });

      setDropdownOptions({
        riskStatementOptions: riskStatementOpts,
        labelDeclarationOptions: labelDeclarationOpts,
        specifyOptions: specifyOpts
      });

      console.log('✅ [useAllergenEditData] Dropdown options loaded successfully');

    } catch (error) {
      console.error('❌ [useAllergenEditData] Error fetching dropdown options:', error);
      handleApiError(
        error,
        errorTexts?.FAILED_TO_LOAD_DROPDOWN_OPTIONS || "Failed to load dropdown options"
      );
    } finally {
      setIsLoadingOptions(false);
      setIsLoading(false); // Global loading indicator
    }
  }, [open, allergenData?.id, allergenData?.label, setIsLoading, errorTexts]);

  /**
   * Fetch full allergen details including attachments
   * API: GET /ProductVendorAggregator/allergenDeclaration/{id}
   */
  const fetchAllergenDetail = useCallback(async () => {
    // Only fetch if modal is open, allergen has an allergenDeclarationId, and we haven't loaded yet
    if (!open || !allergenData?.allergenDeclarationId || hasLoadedDetail) {
      return;
    }

    setIsLoadingDetail(true);
    setIsLoading(true); // Global loading indicator
    
    try {
      console.log('🔍 [useAllergenEditData] Fetching full allergen details for ID:', allergenData.allergenDeclarationId);
      
      const detailResponse: AllergenDetailResponse = await AllergensAndSensitivitiesService.getAllergenDeclarationById(
        allergenData.allergenDeclarationId
      );
      
      console.log('✅ [useAllergenEditData] Allergen detail loaded successfully:', detailResponse);
      
      setAllergenDetail(detailResponse);
      setHasLoadedDetail(true); // Mark as loaded to prevent re-fetching

    } catch (error) {
      console.error('❌ [useAllergenEditData] Error fetching allergen detail:', error);
      handleApiError(
        error,
        errorTexts?.FAILED_TO_LOAD_ALLERGEN_DETAILS || "Failed to load allergen details"
      );
    } finally {
      setIsLoadingDetail(false);
      setIsLoading(false); // Global loading indicator
    }
  }, [open, allergenData?.allergenDeclarationId, hasLoadedDetail, setIsLoading, errorTexts]);

  // Fetch dropdown options when modal opens
  useEffect(() => {
    fetchDropdownOptions();
  }, [fetchDropdownOptions]);

  // Fetch allergen detail when modal opens (if editing existing allergen)
  useEffect(() => {
    fetchAllergenDetail();
  }, [fetchAllergenDetail]);

  // Reset hasLoadedDetail when modal closes or allergen changes
  useEffect(() => {
    if (!open) {
      setHasLoadedDetail(false);
      setAllergenDetail(null);
    }
  }, [open, allergenData?.allergenDeclarationId]);

  return {
    dropdownOptions,
    allergenDetail,
    isLoadingOptions,
    isLoadingDetail,
    refetchDropdownOptions: fetchDropdownOptions,
    refetchAllergenDetail: fetchAllergenDetail
  };
};

export default useAllergenEditData;

